import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Users, Building2, FileText, FolderOpen, Search, Plus, Shield, Crown, Mail, Trash2, Check, Inbox } from 'lucide-react';

export default function AdminPanelPage() {
  const { apiCall, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filterPlan, setFilterPlan] = useState('');
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [createForm, setCreateForm] = useState({ email: '', first_name: '', last_name: '', company_name: '', password: '', custom_limits: { employees: 999999, pafs: 999999 } });
  const [editPlan, setEditPlan] = useState('');
  const [editLimits, setEditLimits] = useState({ employees: '', pafs: '' });
  const [tab, setTab] = useState('users');
  const [submissions, setSubmissions] = useState([]);
  const [selectedSub, setSelectedSub] = useState(null);

  const fetchSubmissions = useCallback(async () => {
    try {
      const res = await apiCall('get', '/contact/submissions');
      setSubmissions(res.data.submissions || []);
    } catch {
      toast.error('Failed to load contact submissions');
    }
  }, [apiCall]);

  const updateSubmissionStatus = async (id, status) => {
    try {
      await apiCall('patch', `/contact/submissions/${id}`, { status });
      toast.success(`Marked as ${status}`);
      fetchSubmissions();
      if (selectedSub && selectedSub.id === id) {
        setSelectedSub({ ...selectedSub, status });
      }
    } catch {
      toast.error('Failed to update status');
    }
  };

  const deleteSubmission = async (id) => {
    if (!window.confirm('Delete this submission?')) return;
    try {
      await apiCall('delete', `/contact/submissions/${id}`);
      toast.success('Submission deleted');
      setSelectedSub(null);
      fetchSubmissions();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const fetchStats = useCallback(async () => {
    try {
      const res = await apiCall('get', '/admin/stats');
      setStats(res.data);
    } catch { /* ignore */ }
  }, [apiCall]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.set('search', search);
      if (filterPlan) params.set('account_type', filterPlan);
      const res = await apiCall('get', `/admin/users?${params}`);
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [apiCall, page, search, filterPlan]);

  useEffect(() => { fetchStats(); fetchUsers(); }, [fetchStats, fetchUsers]);
  useEffect(() => { if (tab === 'submissions') fetchSubmissions(); }, [tab, fetchSubmissions]);

  const handleCreateEnterprise = async () => {
    if (!createForm.email) return toast.error('Email is required');
    try {
      const res = await apiCall('post', '/admin/users/create-enterprise', createForm);
      toast.success(`Enterprise account created! Temp password: ${res.data.temp_password}`, { duration: 10000 });
      setCreateOpen(false);
      setCreateForm({ email: '', first_name: '', last_name: '', company_name: '', password: '', custom_limits: { employees: 999999, pafs: 999999 } });
      fetchUsers();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create account');
    }
  };

  const handleUpdatePlan = async () => {
    if (!editUser || !editPlan) return;
    try {
      const body = { account_type: editPlan };
      if (editPlan === 'enterprise') {
        body.custom_limits = { employees: parseInt(editLimits.employees) || 999999, pafs: parseInt(editLimits.pafs) || 999999 };
      }
      await apiCall('put', `/admin/users/${editUser.user_id}/plan`, body);
      toast.success(`Plan updated to ${editPlan}`);
      setEditUser(null);
      fetchUsers();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update plan');
    }
  };

  const openEditPlan = (u) => {
    setEditUser(u);
    setEditPlan(u.account_type);
    setEditLimits({ employees: u.custom_limits?.employees || '', pafs: u.custom_limits?.pafs || '' });
  };

  if (!user?.is_superadmin) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Shield className="w-12 h-12 text-[#64748B] mx-auto mb-4" strokeWidth={1.5} />
          <h2 className="text-lg font-semibold text-[#0F172A] mb-2">Access Denied</h2>
          <p className="text-[#64748B]">Super admin privileges required.</p>
        </div>
      </div>
    );
  }

  const planBadge = (type) => {
    const styles = {
      free: 'bg-gray-100 text-gray-700',
      starter: 'bg-blue-100 text-blue-700',
      professional: 'bg-indigo-100 text-indigo-700',
      enterprise: 'bg-amber-100 text-amber-700',
    };
    return <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${styles[type] || styles.free}`}>{type}</span>;
  };

  return (
    <div className="space-y-6" data-testid="admin-panel">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#0F172A] tracking-tight">Admin Panel</h1>
          <p className="text-sm text-[#64748B] mt-1">Manage accounts, plans, and enterprise customers</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="bg-[#4F46E5] hover:bg-[#4338CA] text-white rounded-xl" data-testid="create-enterprise-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Enterprise Account
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Users', value: stats.total_users, icon: Users, color: 'text-[#4F46E5]' },
            { label: 'Total Employees', value: stats.total_employees, icon: Building2, color: 'text-emerald-600' },
            { label: 'Total PAFs', value: stats.total_pafs, icon: FolderOpen, color: 'text-amber-600' },
            { label: 'Total Documents', value: stats.total_documents, icon: FileText, color: 'text-sky-600' },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl border border-indigo-100/50 p-5" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
              <div className="flex items-center gap-3 mb-3">
                <s.icon className={`w-5 h-5 ${s.color}`} strokeWidth={1.5} />
                <span className="text-xs text-[#64748B] font-medium">{s.label}</span>
              </div>
              <p className="text-2xl font-bold text-[#0F172A]">{s.value}</p>
            </div>
          ))}
          <div className="col-span-2 md:col-span-4 grid grid-cols-4 gap-4">
            {Object.entries(stats.by_plan).map(([plan, count]) => (
              <div key={plan} className="bg-white rounded-xl border border-indigo-100/50 p-4 text-center">
                {planBadge(plan)}
                <p className="text-xl font-bold text-[#0F172A] mt-2">{count}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="bg-white border border-indigo-100/50 p-1 rounded-xl" data-testid="admin-tabs">
          <TabsTrigger value="users" className="rounded-lg data-[state=active]:bg-indigo-600 data-[state=active]:text-white" data-testid="tab-users">
            <Users className="w-4 h-4 mr-2" /> Users
          </TabsTrigger>
          <TabsTrigger value="submissions" className="rounded-lg data-[state=active]:bg-indigo-600 data-[state=active]:text-white" data-testid="tab-submissions">
            <Inbox className="w-4 h-4 mr-2" /> Contact Submissions
            {submissions.filter(s => s.status === 'new').length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                {submissions.filter(s => s.status === 'new').length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="space-y-4 mt-4">
      {/* Filters */}
      <div className="flex items-center gap-3 bg-white rounded-xl border border-indigo-100/50 p-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
          <Input
            placeholder="Search by email or name..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-9 rounded-lg border-[#E2E8F0] h-10"
            data-testid="admin-search"
          />
        </div>
        <Select value={filterPlan || 'all'} onValueChange={(v) => { setFilterPlan(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-[160px] rounded-lg border-[#E2E8F0] h-10" data-testid="admin-plan-filter">
            <SelectValue placeholder="All plans" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Plans</SelectItem>
            <SelectItem value="free">Free</SelectItem>
            <SelectItem value="starter">Starter</SelectItem>
            <SelectItem value="professional">Professional</SelectItem>
            <SelectItem value="enterprise">Enterprise</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl border border-indigo-100/50 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#F1F5F9]">
              <th className="text-left px-5 py-3 text-xs font-semibold text-[#64748B] uppercase tracking-wider">User</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-[#64748B] uppercase tracking-wider">Plan</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-[#64748B] uppercase tracking-wider">Employees</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-[#64748B] uppercase tracking-wider">PAFs</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-[#64748B] uppercase tracking-wider">Joined</th>
              <th className="text-right px-5 py-3 text-xs font-semibold text-[#64748B] uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id} className="border-b border-[#F8FAFF] hover:bg-[#F8FAFF] transition-colors" data-testid={`user-row-${u.user_id}`}>
                <td className="px-5 py-3.5">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center text-white text-xs font-bold">
                      {(u.first_name?.[0] || u.email[0]).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[#0F172A]">
                        {u.first_name || u.last_name ? `${u.first_name} ${u.last_name}`.trim() : u.email}
                        {u.is_superadmin && <Crown className="inline w-3.5 h-3.5 ml-1 text-amber-500" />}
                      </p>
                      <p className="text-xs text-[#94A3B8]">{u.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3.5">{planBadge(u.account_type)}</td>
                <td className="px-5 py-3.5 text-sm text-[#0F172A]">{u.employees_count}</td>
                <td className="px-5 py-3.5 text-sm text-[#0F172A]">{u.pafs_count}</td>
                <td className="px-5 py-3.5 text-xs text-[#94A3B8]">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                <td className="px-5 py-3.5 text-right">
                  <Button variant="outline" size="sm" className="rounded-lg text-xs" onClick={() => openEditPlan(u)} data-testid={`edit-plan-${u.user_id}`}>
                    Change Plan
                  </Button>
                </td>
              </tr>
            ))}
            {!loading && users.length === 0 && (
              <tr><td colSpan={6} className="text-center py-12 text-sm text-[#94A3B8]">No users found</td></tr>
            )}
          </tbody>
        </table>
        {total > 20 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-[#F1F5F9]">
            <span className="text-xs text-[#94A3B8]">Showing {(page-1)*20+1}–{Math.min(page*20, total)} of {total}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p-1)}>Previous</Button>
              <Button variant="outline" size="sm" disabled={page * 20 >= total} onClick={() => setPage(p => p+1)}>Next</Button>
            </div>
          </div>
        )}
      </div>
        </TabsContent>

        {/* Contact Submissions Tab */}
        <TabsContent value="submissions" className="space-y-4 mt-4">
          <div className="bg-white rounded-xl border border-indigo-100/50 overflow-hidden" data-testid="submissions-panel">
            {submissions.length === 0 ? (
              <div className="py-16 text-center text-sm text-[#94A3B8]">
                <Mail className="w-10 h-10 mx-auto mb-3 text-[#CBD5E1]" strokeWidth={1.5} />
                No contact submissions yet
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#F1F5F9]">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-[#64748B] uppercase tracking-wider">Status</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-[#64748B] uppercase tracking-wider">Name</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-[#64748B] uppercase tracking-wider">Email</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-[#64748B] uppercase tracking-wider">Type</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-[#64748B] uppercase tracking-wider">Submitted</th>
                    <th className="text-right px-5 py-3 text-xs font-semibold text-[#64748B] uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((s) => {
                    const statusStyle = s.status === 'new'
                      ? 'bg-red-50 text-red-700'
                      : s.status === 'read'
                      ? 'bg-blue-50 text-blue-700'
                      : 'bg-emerald-50 text-emerald-700';
                    return (
                      <tr key={s.id} className={`border-b border-[#F8FAFF] hover:bg-[#F8FAFF] transition-colors cursor-pointer ${s.status === 'new' ? 'font-medium' : ''}`}
                          onClick={() => { setSelectedSub(s); if (s.status === 'new') updateSubmissionStatus(s.id, 'read'); }}
                          data-testid={`submission-row-${s.id}`}>
                        <td className="px-5 py-3.5">
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${statusStyle}`}>{s.status}</span>
                        </td>
                        <td className="px-5 py-3.5 text-sm text-[#0F172A]">{s.name}</td>
                        <td className="px-5 py-3.5 text-sm text-[#0F172A]">{s.email}</td>
                        <td className="px-5 py-3.5 text-xs text-[#64748B] capitalize">{s.request_type}</td>
                        <td className="px-5 py-3.5 text-xs text-[#94A3B8]">{s.created_at ? new Date(s.created_at).toLocaleString() : '—'}</td>
                        <td className="px-5 py-3.5 text-right">
                          <Button variant="outline" size="sm" className="rounded-lg text-xs" onClick={(e) => { e.stopPropagation(); deleteSubmission(s.id); }} data-testid={`delete-sub-${s.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Submission Detail Dialog */}
      <Dialog open={!!selectedSub} onOpenChange={(open) => { if (!open) setSelectedSub(null); }}>
        <DialogContent className="max-w-lg" data-testid="submission-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-[#0F172A] flex items-center gap-2">
              <Mail className="w-4 h-4 text-indigo-600" />
              {selectedSub?.request_type === 'demo' ? 'Demo Request' : 'Contact Inquiry'}
            </DialogTitle>
            <DialogDescription className="text-sm text-[#64748B]">
              Submitted {selectedSub?.created_at ? new Date(selectedSub.created_at).toLocaleString() : ''}
            </DialogDescription>
          </DialogHeader>
          {selectedSub && (
            <div className="space-y-3 mt-2 text-sm">
              <div><span className="font-semibold text-[#0F172A]">Name: </span><span className="text-[#334155]">{selectedSub.name}</span></div>
              <div><span className="font-semibold text-[#0F172A]">Email: </span><a href={`mailto:${selectedSub.email}`} className="text-indigo-600 hover:underline">{selectedSub.email}</a></div>
              {selectedSub.company && (
                <div><span className="font-semibold text-[#0F172A]">Company: </span><span className="text-[#334155]">{selectedSub.company}</span></div>
              )}
              <div>
                <p className="font-semibold text-[#0F172A] mb-1">Message</p>
                <div className="bg-[#F8FAFF] rounded-lg p-3 text-[#334155] whitespace-pre-wrap border border-indigo-100/50">
                  {selectedSub.message || '—'}
                </div>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-[#F1F5F9]">
                <div className="flex gap-2">
                  {selectedSub.status !== 'resolved' && (
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg" onClick={() => updateSubmissionStatus(selectedSub.id, 'resolved')} data-testid="mark-resolved-btn">
                      <Check className="w-3.5 h-3.5 mr-1" /> Mark Resolved
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="rounded-lg" onClick={() => window.location.href = `mailto:${selectedSub.email}`}>
                    Reply via Email
                  </Button>
                </div>
                <Button size="sm" variant="outline" className="rounded-lg text-red-600 hover:bg-red-50" onClick={() => deleteSubmission(selectedSub.id)}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Enterprise Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg" data-testid="create-enterprise-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-[#0F172A]">Create Enterprise Account</DialogTitle>
            <DialogDescription className="text-sm text-[#64748B]">Set up a new enterprise customer account with custom limits.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">First Name</Label>
                <Input value={createForm.first_name} onChange={(e) => setCreateForm({...createForm, first_name: e.target.value})} className="rounded-xl" data-testid="ent-first-name" />
              </div>
              <div>
                <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Last Name</Label>
                <Input value={createForm.last_name} onChange={(e) => setCreateForm({...createForm, last_name: e.target.value})} className="rounded-xl" data-testid="ent-last-name" />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Email *</Label>
              <Input type="email" value={createForm.email} onChange={(e) => setCreateForm({...createForm, email: e.target.value})} className="rounded-xl" data-testid="ent-email" />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Company Name</Label>
              <Input value={createForm.company_name} onChange={(e) => setCreateForm({...createForm, company_name: e.target.value})} className="rounded-xl" data-testid="ent-company" />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Password (leave blank for auto-generated)</Label>
              <Input type="text" value={createForm.password} onChange={(e) => setCreateForm({...createForm, password: e.target.value})} placeholder="Auto-generated if empty" className="rounded-xl" data-testid="ent-password" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Employee Limit</Label>
                <Input type="number" value={createForm.custom_limits.employees} onChange={(e) => setCreateForm({...createForm, custom_limits: {...createForm.custom_limits, employees: parseInt(e.target.value) || 0}})} className="rounded-xl" data-testid="ent-emp-limit" />
              </div>
              <div>
                <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">PAF Limit</Label>
                <Input type="number" value={createForm.custom_limits.pafs} onChange={(e) => setCreateForm({...createForm, custom_limits: {...createForm.custom_limits, pafs: parseInt(e.target.value) || 0}})} className="rounded-xl" data-testid="ent-paf-limit" />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" className="rounded-xl" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button className="bg-[#4F46E5] hover:bg-[#4338CA] text-white rounded-xl" onClick={handleCreateEnterprise} data-testid="create-enterprise-submit">
                Create Account
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Plan Dialog */}
      <Dialog open={!!editUser} onOpenChange={(open) => { if (!open) setEditUser(null); }}>
        <DialogContent className="max-w-md" data-testid="edit-plan-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-[#0F172A]">Change Plan</DialogTitle>
            <DialogDescription className="text-sm text-[#64748B]">
              Update plan for <span className="font-medium text-[#0F172A]">{editUser?.email}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Plan</Label>
              <Select value={editPlan} onValueChange={setEditPlan}>
                <SelectTrigger className="rounded-xl" data-testid="edit-plan-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="starter">Starter</SelectItem>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {editPlan === 'enterprise' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Employee Limit</Label>
                  <Input type="number" value={editLimits.employees} onChange={(e) => setEditLimits({...editLimits, employees: e.target.value})} className="rounded-xl" data-testid="edit-emp-limit" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">PAF Limit</Label>
                  <Input type="number" value={editLimits.pafs} onChange={(e) => setEditLimits({...editLimits, pafs: e.target.value})} className="rounded-xl" data-testid="edit-paf-limit" />
                </div>
              </div>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" className="rounded-xl" onClick={() => setEditUser(null)}>Cancel</Button>
              <Button className="bg-[#4F46E5] hover:bg-[#4338CA] text-white rounded-xl" onClick={handleUpdatePlan} data-testid="update-plan-submit">
                Update Plan
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
